// AvatarPicker.js — El círculo del avatar y, si la página lo permite, la
// galería para cambiarlo.
//
// Los avatares son los del proyecto (`config_json.avatares`) o los cinco de
// serie. Se guarda la ruta elegida en el perfil de la persona, así que el mismo
// dibujo la acompaña en todas las pantallas que muestran su avatar.
import React, { useState } from 'react';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import { BACKEND_BASE, profileService } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { useProject } from '../../contexts/ProjectContext';
import { avataresDeProyecto, urlAvatar } from '../../utils/avatares';

const Marco = styled.div`
  display: inline-block;
  position: relative;
`;

const Circulo = styled.button`
  display: block;
  width: ${p => p.$size || '180px'};
  height: ${p => p.$size || '180px'};
  padding: 0;
  border-radius: 50%;
  border: ${p => p.$borde || '1px solid #dfe3e8'};
  background: ${p => (p.$img ? `url("${p.$img}") center/cover no-repeat` : (p.$bg || 'transparent'))};
  cursor: ${p => (p.$editable ? 'pointer' : 'default')};
  transition: box-shadow 0.2s;
  ${p => p.$editable && '&:hover { box-shadow: 0 0 0 4px rgba(18,52,92,0.12); }'}
  @media (max-width: 600px) { width: 120px; height: 120px; }
`;

const Lapiz = styled.span`
  position: absolute;
  right: 6px;
  bottom: 6px;
  width: 38px;
  height: 38px;
  border-radius: 50%;
  background: ${p => p.$bg || '#12345c'};
  color: #ffffff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  pointer-events: none;
  @media (max-width: 600px) { width: 28px; height: 28px; font-size: 13px; }
`;

const Galeria = styled.div`
  margin-top: 16px;
  padding: 16px;
  border: 1px solid #e4e7ec;
  border-radius: 10px;
  background: #ffffff;
  display: inline-block;
  max-width: 100%;
`;

const GaleriaTitulo = styled.div`
  font-size: 14px;
  color: ${p => p.$color || '#5b6b7f'};
  margin-bottom: 12px;
`;

const Opciones = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  justify-content: center;
`;

const Opcion = styled.button`
  width: ${p => p.$size || '76px'};
  height: ${p => p.$size || '76px'};
  padding: 0;
  border-radius: 50%;
  cursor: pointer;
  background: url("${p => p.$img}") center/cover no-repeat;
  border: 3px solid ${p => (p.$on ? (p.$color || '#12345c') : 'transparent')};
  box-shadow: 0 0 0 1px #e4e7ec;
  transition: transform 0.15s;
  &:hover { transform: translateY(-2px); }
  &:disabled { opacity: 0.6; cursor: default; }
`;

const AvatarPicker = ({
  size,
  borde,
  fondo,
  editable = true,
  colorAcento,
  textoGaleria,
  onCambio,
}) => {
  const { user, updateUser } = useAuth();
  const { proyecto } = useProject();
  const [abierta, setAbierta] = useState(false);
  const [guardando, setGuardando] = useState(null);

  const opciones = avataresDeProyecto(proyecto);
  const actual = user?.avatar_url || '';

  const elegir = async (ruta) => {
    setGuardando(ruta);
    try {
      await profileService.updateProfile({ avatar_url: ruta });
      if (updateUser) updateUser({ avatar_url: ruta });
      if (onCambio) onCambio(ruta);
      setAbierta(false);
    } catch (e) {
      toast.error(e.response?.data?.message || 'No se pudo cambiar el avatar');
    }
    setGuardando(null);
  };

  // Sin avatar propio se muestra el primero de la galería, para que el círculo
  // nunca quede vacío.
  const imgActual = urlAvatar(actual || opciones[0], BACKEND_BASE);

  return (
    <div>
      <Marco>
        <Circulo
          type="button"
          $size={size}
          $borde={borde}
          $bg={fondo}
          $img={imgActual}
          $editable={editable}
          onClick={() => editable && setAbierta(v => !v)}
          aria-label={editable ? 'Cambiar avatar' : 'Avatar'}
          disabled={!editable}
        />
        {editable && <Lapiz $bg={colorAcento}>✎</Lapiz>}
      </Marco>

      {editable && abierta && (
        <div>
          <Galeria>
            <GaleriaTitulo>{textoGaleria || 'Elige tu avatar'}</GaleriaTitulo>
            <Opciones>
              {opciones.map(ruta => (
                <Opcion
                  key={ruta}
                  type="button"
                  onClick={() => elegir(ruta)}
                  disabled={!!guardando}
                  $img={urlAvatar(ruta, BACKEND_BASE)}
                  $on={actual === ruta}
                  $color={colorAcento}
                  aria-label={`Avatar ${ruta.split('/').pop()}`}
                />
              ))}
            </Opciones>
          </Galeria>
        </div>
      )}
    </div>
  );
};

export default AvatarPicker;
